import type { EChartsOption } from 'echarts';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../../contexts/ThemeContext';

import DashboardEChart from './DashboardEChart';
import { chartTooltipOption, getDashboardChartColors } from './dashboardChartTheme';

export interface DashboardBarPoint {
  opt: number;
  app: number;
  off: number;
}

export interface DashboardStackedBarChartProps {
  data: DashboardBarPoint[];
  monthLabels: string[];
  height?: number;
  className?: string;
}

const DashboardStackedBarChart: React.FC<DashboardStackedBarChartProps> = ({
  data,
  monthLabels,
  height = 240,
  className,
}) => {
  const { t } = useTranslation();
  const optLabel = t('dashboard.overview.chartOptimizations', 'Optimizations');
  const appLabel = t('dashboard.overview.chartApplications', 'Applications');
  const offLabel = t('dashboard.overview.chartOffers', 'Offers');
  const { theme } = useTheme();

  const option = useMemo((): EChartsOption => {
    const colors = getDashboardChartColors();
    const maxTotal = Math.max(...data.map((d) => d.opt + d.app + d.off), 1);
    const yMax = Math.max(30, Math.ceil((maxTotal * 1.2) / 5) * 5);

    return {
      grid: { left: 12, right: 12, top: 32, bottom: 42, containLabel: false },
      tooltip: chartTooltipOption(theme, {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      }),
      legend: {
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 16,
        icon: 'circle',
        textStyle: { color: colors.textMuted, fontSize: 10, fontWeight: 600 },
        data: [optLabel, appLabel, offLabel],
      },
      xAxis: {
        type: 'category',
        data: monthLabels,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: colors.textMuted, fontSize: 10, fontWeight: 600 },
      },
      yAxis: {
        type: 'value',
        max: yMax,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: {
          lineStyle: { color: colors.border, type: 'dashed', opacity: 0.5 },
        },
      },
      series: [
        {
          name: optLabel,
          type: 'bar',
          stack: 'total',
          barMaxWidth: 36,
          barGap: '30%',
          itemStyle: {
            borderRadius: [0, 0, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: colors.chartOpt },
                { offset: 1, color: `${colors.chartOpt}99` },
              ],
            },
          },
          data: data.map((d) => d.opt),
        },
        {
          name: appLabel,
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: colors.chartApp },
                { offset: 1, color: `${colors.chartApp}99` },
              ],
            },
          },
          data: data.map((d) => d.app),
        },
        {
          name: offLabel,
          type: 'bar',
          stack: 'total',
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: colors.chartOff },
                { offset: 1, color: `${colors.chartOff}99` },
              ],
            },
          },
          data: data.map((d) => d.off),
        },
      ],
      animationDuration: 800,
      animationEasing: 'cubicOut',
    };
  }, [data, monthLabels, optLabel, appLabel, offLabel, theme]);

  return <DashboardEChart option={option} height={height} className={className} />;
};

export default DashboardStackedBarChart;
